import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Sparkles,
  FileText,
  CornerDownLeft,
  Loader2,
  ArrowLeft,
  X,
} from 'lucide-react';
import { api } from '@/lib/api';
import type { SearchResult, AskResult } from '@/lib/types';

/* ---------------------------------------------------------------------------
   SearchPalette — Cmd-K / Ctrl-K command palette for STR Wiki.

   Two modes in one box:
     • "results" — debounced full-text search  → api.search({ q })
     • "answer"  — ask the AI a question        → api.ask({ q })

   Mount ONCE, high in the tree (AppLayout):

       import { SearchPalette, SearchTrigger, openSearch } from
         '@/components/SearchPalette';

       // somewhere persistent (renders the overlay, owns the ⌘K listener):
       <SearchPalette />

       // optional top-bar button that opens it:
       <SearchTrigger />

   `openSearch()` / `openSearch(prefill)` can be called from anywhere to open
   the palette imperatively (decoupled via a window CustomEvent — no context
   provider required).
   --------------------------------------------------------------------------- */

// Response shapes come from the shared contract in '@/lib/types':
//   api.search(q) → { results: SearchResult[] }
//   api.ask(q)    → AskResult { answer, citations: AskCitation[] }
type SearchHit = SearchResult;

// Flip to true once the OpenAI key + embeddings are configured (see PUBLISH-TO-WIKI / memory).
const AI_SEARCH_ENABLED = false;

// ---- imperative open channel -----------------------------------------------
const OPEN_EVENT = 'str:open-search';

/** Open the command palette from anywhere. Optional prefill query string. */
export function openSearch(prefill?: string) {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: { prefill } }));
}

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

// ---- top-bar trigger button ------------------------------------------------
export function SearchTrigger({ className = '' }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={() => openSearch()}
      className={
        'group flex w-full max-w-sm items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ' +
        className
      }
      style={{
        borderColor: 'var(--border)',
        background: 'var(--surface-2)',
        color: 'var(--ink-faint)',
      }}
      aria-label="Search (Command K)"
    >
      <Search size={15} className="shrink-0" />
      <span className="flex-1 truncate text-left">
        {AI_SEARCH_ENABLED ? 'Search or ask AI…' : 'Search — coming soon'}
      </span>
      <kbd
        className="hidden shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px] sm:inline-block"
        style={{ background: 'var(--surface-3)', color: 'var(--ink-muted)' }}
      >
        {isMac ? '⌘K' : 'Ctrl K'}
      </kbd>
    </button>
  );
}

// ---- combined navigable item model -----------------------------------------
type NavItem =
  | { kind: 'result'; hit: SearchHit }
  | { kind: 'ask'; query: string };

// ---- palette ----------------------------------------------------------------
export function SearchPalette() {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [mode, setMode] = useState<'results' | 'answer'>('results');

  const [results, setResults] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);

  const [answer, setAnswer] = useState<AskResult | null>(null);
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [askedQuery, setAskedQuery] = useState('');

  const [activeIndex, setActiveIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchSeq = useRef(0);
  const askSeq = useRef(0);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setSearching(false);
    setAnswer(null);
    setAsking(false);
    setAskError(null);
    setAskedQuery('');
    setMode('results');
    setActiveIndex(0);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // ---- global open: ⌘K / Ctrl-K + imperative event ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && k === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ prefill?: string }>).detail;
      setOpen(true);
      if (detail?.prefill) setQuery(detail.prefill);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_EVENT, onOpen as EventListener);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_EVENT, onOpen as EventListener);
    };
  }, []);

  // ---- focus input + lock scroll when open; clean up on close ----
  useEffect(() => {
    if (open) {
      const t = window.setTimeout(() => inputRef.current?.focus(), 20);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        window.clearTimeout(t);
        document.body.style.overflow = prevOverflow;
      };
    }
    // fully reset a beat after closing so the exit doesn't flash empty
    const t = window.setTimeout(reset, 150);
    return () => window.clearTimeout(t);
  }, [open, reset]);

  // ---- debounced search ----
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const seq = ++searchSeq.current;
    const t = window.setTimeout(async () => {
      try {
        const data = await api.search(q);
        if (seq !== searchSeq.current) return; // stale
        setResults(data?.results ?? []);
      } catch {
        if (seq !== searchSeq.current) return;
        setResults([]);
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [query, open]);

  // reset highlight whenever the result set changes
  useEffect(() => {
    setActiveIndex(0);
  }, [results, query]);

  // ---- ask AI ----
  const runAsk = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setMode('answer');
    setAnswer(null);
    setAskError(null);
    setAsking(true);
    setAskedQuery(q);
    const seq = ++askSeq.current;
    try {
      const data = await api.ask(q);
      if (seq !== askSeq.current) return;
      setAnswer(data);
    } catch {
      if (seq !== askSeq.current) return;
      setAskError('Could not reach the assistant. Please try again.');
    } finally {
      if (seq === askSeq.current) setAsking(false);
    }
  }, [query]);

  const goToDoc = useCallback(
    (nodeId: string) => {
      close();
      navigate(`/doc/${nodeId}`);
    },
    [close, navigate],
  );

  // ---- navigable list = results, then a trailing "Ask AI" action ----
  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = results.map((hit) => ({ kind: 'result', hit }));
    if (query.trim()) items.push({ kind: 'ask', query: query.trim() });
    return items;
  }, [results, query]);

  const activate = useCallback(
    (index: number) => {
      const item = navItems[index];
      if (!item) return;
      if (item.kind === 'result') goToDoc(item.hit.nodeId);
      else runAsk();
    },
    [navItems, goToDoc, runAsk],
  );

  // keep highlighted row scrolled into view
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-idx="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // ---- key handling inside the box ----
  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (mode === 'answer') {
        setMode('results');
        return;
      }
      close();
      return;
    }
    // Cmd/Ctrl+Enter is always "Ask AI"
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      runAsk();
      return;
    }
    if (mode !== 'results') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(navItems.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(activeIndex);
    }
  };

  if (!open) return null;

  // Search + Ask AI are gated until embeddings/OpenAI are wired — show "coming soon".
  if (!AI_SEARCH_ENABLED) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[16vh] pb-8"
        role="dialog"
        aria-modal="true"
        aria-label="Search coming soon"
        onMouseDown={close}
      >
        <div className="absolute inset-0 backdrop-blur-[2px]" style={{ background: 'rgba(10,12,16,0.45)' }} />
        <div
          className="relative z-10 w-full max-w-md rounded-xl p-6 text-center shadow-2xl"
          style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full" style={{ background: 'var(--surface-3)' }}>
            <Sparkles size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <h2 className="text-base font-semibold">Search &amp; Ask AI</h2>
          <p className="mx-auto mt-1 max-w-xs text-sm" style={{ color: 'var(--ink-muted)' }}>
            Coming soon — semantic search and “ask the wiki” across all your pages, with citations.
          </p>
          <button type="button" onClick={close} className="btn btn-ghost mt-4">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh] pb-8"
      role="dialog"
      aria-modal="true"
      aria-label="Search and ask"
      onMouseDown={close}
    >
      {/* backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-[2px]"
        style={{ background: 'rgba(10,12,16,0.45)' }}
      />

      {/* box */}
      <div
        className="relative z-10 flex max-h-[70vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl shadow-2xl"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* input row */}
        <div
          className="flex items-center gap-3 px-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          {mode === 'answer' ? (
            <button
              type="button"
              onClick={() => setMode('results')}
              className="shrink-0"
              style={{ color: 'var(--ink-muted)' }}
              aria-label="Back to results"
            >
              <ArrowLeft size={18} />
            </button>
          ) : (
            <Search size={18} className="shrink-0" style={{ color: 'var(--ink-faint)' }} />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (mode === 'answer') setMode('results');
            }}
            onKeyDown={onInputKeyDown}
            placeholder="Search pages, or ask a question…"
            className="flex-1 bg-transparent py-4 text-[15px] outline-none"
            style={{ color: 'var(--ink)' }}
            spellCheck={false}
            autoComplete="off"
          />
          {(searching || asking) && (
            <Loader2 size={16} className="shrink-0 animate-spin" style={{ color: 'var(--ink-faint)' }} />
          )}
          <button
            type="button"
            onClick={close}
            className="shrink-0 rounded p-1 transition-colors"
            style={{ color: 'var(--ink-faint)' }}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* body */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {mode === 'answer'
            ? renderAnswer()
            : renderResults()}
        </div>

        {/* footer hint bar */}
        <div
          className="flex items-center gap-4 px-4 py-2 text-[11px]"
          style={{
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
            color: 'var(--ink-faint)',
          }}
        >
          <Hint keys={['↑', '↓']} label="Navigate" />
          <Hint keys={['↵']} label="Open" />
          <Hint keys={[isMac ? '⌘' : 'Ctrl', '↵']} label="Ask AI" />
          <span className="ml-auto flex items-center gap-1">
            <Sparkles size={12} /> Powered by STR AI
          </span>
        </div>
      </div>
    </div>
  );

  // ---------- render helpers ----------
  function renderResults() {
    const q = query.trim();

    if (!q) {
      return (
        <div
          className="flex flex-col items-center gap-2 px-6 py-14 text-center text-sm"
          style={{ color: 'var(--ink-faint)' }}
        >
          <Search size={22} />
          <p>Search across every page you can see.</p>
          <p className="text-[13px]">
            Type a question and press{' '}
            <kbd
              className="rounded px-1 py-0.5 font-mono text-[11px]"
              style={{ background: 'var(--surface-3)', color: 'var(--ink-muted)' }}
            >
              {isMac ? '⌘↵' : 'Ctrl ↵'}
            </kbd>{' '}
            to ask the AI.
          </p>
        </div>
      );
    }

    return (
      <div className="py-2">
        {results.length > 0 && (
          <div
            className="px-4 pb-1 pt-1 text-[11px] font-medium uppercase tracking-wide"
            style={{ color: 'var(--ink-faint)' }}
          >
            Pages
          </div>
        )}

        {results.map((hit, i) => {
          const active = activeIndex === i;
          return (
            <button
              key={hit.nodeId}
              data-idx={i}
              type="button"
              onMouseMove={() => setActiveIndex(i)}
              onClick={() => goToDoc(hit.nodeId)}
              className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors"
              style={{ background: active ? 'var(--surface-3)' : 'transparent' }}
            >
              <FileText
                size={16}
                className="mt-0.5 shrink-0"
                style={{ color: active ? 'var(--accent)' : 'var(--ink-faint)' }}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium" style={{ color: 'var(--ink)' }}>
                  {hit.title}
                </span>
                {hit.snippet && (
                  <span className="mt-0.5 block truncate text-[13px]" style={{ color: 'var(--ink-muted)' }}>
                    {hit.snippet}
                  </span>
                )}
              </span>
              {active && (
                <CornerDownLeft size={14} className="mt-1 shrink-0" style={{ color: 'var(--ink-faint)' }} />
              )}
            </button>
          );
        })}

        {!searching && results.length === 0 && (
          <div className="px-4 py-6 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
            No pages match “{q}”.
          </div>
        )}

        {/* Ask AI action (last navigable item) */}
        {q && renderAskAction()}
      </div>
    );
  }

  function renderAskAction() {
    const idx = navItems.length - 1;
    const active = activeIndex === idx;
    return (
      <>
        <div className="my-1 mx-4 border-t" style={{ borderColor: 'var(--border)' }} />
        <button
          data-idx={idx}
          type="button"
          onMouseMove={() => setActiveIndex(idx)}
          onClick={runAsk}
          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
          style={{ background: active ? 'var(--surface-3)' : 'transparent' }}
        >
          <span
            className="grid h-7 w-7 shrink-0 place-items-center rounded-md"
            style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}
          >
            <Sparkles size={15} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium" style={{ color: 'var(--ink)' }}>
              Ask AI
            </span>
            <span className="block truncate text-[13px]" style={{ color: 'var(--ink-muted)' }}>
              “{query.trim()}”
            </span>
          </span>
          <kbd
            className="shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]"
            style={{ background: 'var(--surface-3)', color: 'var(--ink-muted)' }}
          >
            {isMac ? '⌘↵' : 'Ctrl ↵'}
          </kbd>
        </button>
      </>
    );
  }

  function renderAnswer() {
    return (
      <div className="px-4 py-4">
        <div className="mb-3 flex items-center gap-2 text-[13px]" style={{ color: 'var(--ink-muted)' }}>
          <Sparkles size={14} style={{ color: 'var(--accent)' }} />
          <span className="min-w-0 truncate">
            {askedQuery ? <>Answering: “{askedQuery}”</> : 'Ask AI'}
          </span>
        </div>

        {asking && (
          <div className="space-y-2 py-2">
            <div className="h-3 w-11/12 animate-pulse rounded" style={{ background: 'var(--surface-3)' }} />
            <div className="h-3 w-full animate-pulse rounded" style={{ background: 'var(--surface-3)' }} />
            <div className="h-3 w-4/5 animate-pulse rounded" style={{ background: 'var(--surface-3)' }} />
          </div>
        )}

        {askError && (
          <div className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            {askError}
          </div>
        )}

        {!asking && answer && (
          <>
            <div
              className="whitespace-pre-wrap text-[14px] leading-relaxed"
              style={{ color: 'var(--ink)' }}
            >
              {answer.answer}
            </div>

            {answer.citations?.length > 0 && (
              <div className="mt-4">
                <div
                  className="mb-1.5 text-[11px] font-medium uppercase tracking-wide"
                  style={{ color: 'var(--ink-faint)' }}
                >
                  Sources
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {answer.citations.map((c, i) => (
                    <button
                      key={`${c.nodeId}-${i}`}
                      type="button"
                      onClick={() => goToDoc(c.nodeId)}
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] transition-colors"
                      style={{
                        border: '1px solid var(--border)',
                        background: 'var(--surface-2)',
                        color: 'var(--ink)',
                      }}
                    >
                      <FileText size={12} style={{ color: 'var(--ink-faint)' }} />
                      <span className="max-w-[220px] truncate">{c.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

// ---- tiny footer hint pill --------------------------------------------------
function Hint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((k) => (
        <kbd
          key={k}
          className="rounded px-1 py-0.5 font-mono text-[10px]"
          style={{ background: 'var(--surface-3)', color: 'var(--ink-muted)' }}
        >
          {k}
        </kbd>
      ))}
      <span>{label}</span>
    </span>
  );
}

export default SearchPalette;
